import React, { useEffect, useState, createRef } from 'react';
import HyperLink from 'components/HyperLink';
import { Container, Header, Links } from './styles';
import Text from 'components/Text';
import { truncateText } from 'utils/text';
import Ticker from 'react-ticker';
import PropTypes from 'prop-types';

const RichTextTableOfContents = ({ data, min, max, classNames }) => {
  const [hoveredIndex, setHoveredIndex] = useState(-1);
  const [activeIndex, setActiveIndex] = useState(0);
  const [scrollTop, setScrollTop] = useState(true);
  const [scrollBottom, setScrollBottom] = useState(false);
  const linkRefs = data?.map(() => createRef(null));
  const scrollContainer = createRef(null);
  const numberOfLinks = data?.length;

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          const id = entry.target.getAttribute('id');
          if (entry.isIntersecting) {
            const index = data?.findIndex(ele => id === ele.anchor.split('=#')[1]);
            setActiveIndex(index);
          }
        });
      },
      { rootMargin: `0% 0% -80% 0%` }
    );

    // Track all h2's that have an `id` applied
    document.querySelectorAll("div[id^='toc']").forEach(div => {
      observer.observe(div);
    });
  }, []);

  useEffect(() => {
    scrollContainer.current.onwheel = e => {
      e.preventDefault();
      window.scrollBy(0, e.deltaY);
    };
  }, [scrollContainer]);

  useEffect(() => {
    if (numberOfLinks > max - 1) {
      let top = 0;
      if (activeIndex < min) {
        top = 0;
      } else {
        top = linkRefs[activeIndex]?.current?.offsetTop - scrollContainer?.current?.offsetTop;
      }
      scrollContainer?.current?.scrollTo({ top: top, behavior: 'smooth' });
    }
  }, [activeIndex]);

  const handleScroll = e => {
    const bottom = e.target.scrollHeight - e.target.scrollTop === e.target.clientHeight;
    if (bottom) {
      setScrollBottom(true);
    } else {
      setScrollBottom(false);
    }
    if (e.target.scrollTop < 5) {
      setScrollTop(true);
    } else {
      setScrollTop(false);
    }
  };

  return (
    <Container
      id="table-of-contents"
      scrollTop={scrollTop}
      scrollBottom={scrollBottom}
      onScroll={numberOfLinks >= 6 ? handleScroll : undefined}
      numberOfLinks={numberOfLinks}
      className={classNames}
      min={min}
      max={max}
    >
      <Header>
        <Text className="toc--heading" size="20px" type="h6">
          Table of Contents
        </Text>
      </Header>
      {data && (
        <Links
          activeIndex={activeIndex}
          ref={scrollContainer}
          numberOfLinks={numberOfLinks}
          min={min}
          max={max}
        >
          <div className="toc--active-bar"></div>
          {data.map((ele, index) => (
            <HyperLink
              href={ele.anchor}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(-1)}
              key={index}
            >
              <div className="toc--link-container">
                {hoveredIndex === index ? (
                  <Ticker mode="await" speed={3}>
                    {() => (
                      <TextLink
                        index={index}
                        activeIndex={activeIndex}
                        linkRefs={linkRefs}
                        title={ele.title}
                      />
                    )}
                  </Ticker>
                ) : (
                  <TextLink
                    index={index}
                    activeIndex={activeIndex}
                    linkRefs={linkRefs}
                    title={truncateText(ele.title, 32)}
                  />
                )}
              </div>
            </HyperLink>
          ))}
        </Links>
      )}
    </Container>
  );
};

RichTextTableOfContents.defaultProps = {
  data: [],
  min: 4,
  max: 7,
};

RichTextTableOfContents.propTypes = {
  data: PropTypes.array,
  min: PropTypes.number,
  max: PropTypes.number,
};

const TextLink = ({ index, activeIndex, linkRefs, title, ...otherProps }) => {
  return (
    <Text
      size="16px"
      type="p"
      className={`toc--link${activeIndex === index ? ' active' : ''}`}
      key={index}
      {...otherProps}
    >
      <div
        style={{
          display: 'table-cell',
          verticalAlign: 'middle',
        }}
        ref={linkRefs[index]}
      >
        {index + 1}: {title}
      </div>
    </Text>
  );
};

export default RichTextTableOfContents;
